import { useEffect, useMemo, useRef, useState } from 'react';
import { saveOrder, type CatalogItem, type Customer, type LicenceStatus, type Order } from '@makila/pos-core';
import { CalendarDays, LayoutDashboard, ListOrdered, Moon, Plug, RefreshCw, ShoppingCart, Sun, Undo2, Wallet, X } from 'lucide-react';
import { AgendaView } from '../components/agenda/AgendaView';
import type { AgendaClient, AgendaResource, AgendaService, Appointment } from '../components/agenda/types';
import { CustomerDialog } from '../components/CustomerDialog';
import { HardwareDialog } from '../components/HardwareDialog';
import { ManagerApproval } from '../components/ManagerApproval';
import { Rail, RailItem, RailTool } from '../components/Rail';
import type { Staff } from '../components/StaffPicker';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, identity, terminalApi } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { SyncStatus } from '../lib/sync';
import { useTheme } from '../lib/theme';
import type { Operator } from './Lock';
import type { Product } from './Sale';

const data = <T,>(i: CatalogItem) => i.data as unknown as T;

interface Props {
  store: IdbTerminalStore; device: DeviceState; licence: LicenceStatus | null; sync: SyncStatus | null; operator: Operator;
  onCharge: (o: Order) => void; onQueue?: () => void;
  onCounter: (open?: 'cash' | 'return') => void; onLock: () => void; onSyncNow: () => void; onGestao?: () => void;
}

/** Agenda na caixa (salão, spa, unhas): marcações com rede; sem rede, a última agenda vista. */
export function AgendaScreen(p: Props) {
  const [staff, setStaff] = useState<Staff[]>([]);
  const [services, setServices] = useState<AgendaService[]>([]);
  const [resources, setResources] = useState<AgendaResource[]>([]);
  const [hwOpen, setHwOpen] = useState(false);
  const [approving, setApproving] = useState<{ why: string; done: (ok: boolean) => void } | null>(null);
  const [picking, setPicking] = useState<((c: { id?: string; name: string; phone: string | null } | null) => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<{ name: string; h: HealthData | null } | null>(null);
  const [dark, toggleTheme] = useTheme();
  const approver = useRef<string | null>(null);
  const offline = p.sync ? !p.sync.online : false;

  useEffect(() => {
    void (async () => {
      setStaff((await p.store.catalog('operator')).map(data<Staff & { sortOrder?: number }>).filter((o) => o.active && o.performsServices)
        .sort((a, b) => ((a as { sortOrder?: number }).sortOrder ?? 0) - ((b as { sortOrder?: number }).sortOrder ?? 0) || a.name.localeCompare(b.name, 'pt')));
      setServices((await p.store.catalog('product')).map(data<Product>).filter((x) => x.active && x.kind === 'service').map((x) => ({ id: x.id, name: x.name, shortName: x.shortName, unitPrice: x.unitPrice, durationMin: x.durationMin })));
    })();
  }, [p.store, p.sync?.lastSyncAt]);
  useEffect(() => {
    if (!p.device.company.modules?.includes('resources')) return;
    void terminalApi<{ items: AgendaResource[] }>(p.device, '/terminal/resources').then((r) => setResources(r.items), () => {});
  }, [p.device]);

  const client: AgendaClient = useMemo(() => {
    const who = () => ({ operatorId: p.operator.id, ...(approver.current ? { approvedBy: approver.current } : {}) });
    const body = (x: object) => JSON.stringify({ ...who(), ...x });
    const wrap = async <T,>(f: () => Promise<T>) => { try { return await f(); } finally { approver.current = null; } };
    return {
      list: async (from, to) => (await terminalApi<{ items: Appointment[] }>(p.device, `/terminal/appointments?from=${from}&to=${to}`)).items,
      create: (a) => wrap(() => terminalApi(p.device, '/terminal/appointments', { method: 'POST', body: body(a) })),
      update: (id, patch) => wrap(async () => (await terminalApi<{ appointment: Appointment }>(p.device, `/terminal/appointments/${id}`, { method: 'PATCH', body: body(patch) })).appointment),
      approve: (why) => new Promise<boolean>((done) => setApproving({ why, done })),
    };
  }, [p.device, p.operator.id]);

  const cache = useMemo(() => ({
    get: (k: string) => p.store.kv<Appointment[]>(k).then((v) => v ?? undefined),
    set: (k: string, v: Appointment[]) => p.store.setKv(k, v),
  }), [p.store]);

  /** Cobrar: uma comanda com os serviços e o profissional; a venda fecha-a e a nuvem conclui a marcação. */
  const charge = async (a: Appointment) => {
    try {
      const now = new Date().toISOString();
      const products = (await p.store.catalog('product')).map(data<Product>);
      const order: Order = {
        id: crypto.randomUUID(), kind: 'queue', tableId: null, label: `Marcação · ${a.customerName}`, guests: null, status: 'open',
        openedBy: p.operator.id, openedAt: now, updatedAt: now, documents: [], terminalId: identity(p.device).terminalId, appointmentId: a.id,
        queue: { ticket: 0, customer: { ...(a.customerId ? { id: a.customerId } : {}), name: a.customerName, phone: a.phone }, preferredStaffId: a.staffId, status: 'serving', servingBy: a.staffId, startedAt: now },
        items: a.services.map((x) => {
          const prod = products.find((y) => y.id === x.productId);
          return {
            id: crypto.randomUUID(), productId: x.productId, description: x.description, unitPrice: x.price, taxRate: prod?.taxRate ?? '16', priceIncludesTax: prod?.priceIncludesTax ?? true,
            exemptionCode: prod?.exemptionCode ?? null, quantity: '1', modifiers: [], note: null, station: null, status: 'new' as const, addedBy: p.operator.id, addedAt: now, updatedAt: now,
            staffId: a.staffId, durationMin: x.durationMin,
          };
        }),
      };
      const saved = await saveOrder(p.store, identity(p.device), order);
      await client.update(a.id, { status: 'in_service', orderId: saved.id }).catch(() => {});
      p.onSyncNow();
      p.onCharge(saved);
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        <RailItem icon={CalendarDays} label="Agenda" active />
        {p.onQueue && <RailItem icon={ListOrdered} label="Fila" onClick={p.onQueue} />}
        <RailItem icon={ShoppingCart} label="Vender" onClick={() => p.onCounter()} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!p.licence?.sell} onClick={() => p.onCounter('return')} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => p.onCounter('cash')} />
      </Rail>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">{p.device.company.tradeName ?? p.device.company.legalName}</div>
            <div className="truncate text-xs text-muted">{p.device.store.name} · {p.device.terminal.name} · {p.operator.name}</div>
          </div>
          <div className="ml-auto"><StatusPill sync={p.sync} /></div>
        </header>
        {error && <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-amber-900"><span className="flex-1">{error}</span><button aria-label="Fechar aviso" onClick={() => setError(null)} className="grid h-10 w-10 place-items-center"><X size={18} /></button></div>}
        <div className="min-h-0 flex-1">
          <AgendaView client={client} staff={staff} services={services} resources={resources} businessName={p.device.company.tradeName ?? p.device.company.legalName}
            offline={offline} cache={cache} onCharge={(a) => void charge(a)}
            onHealth={p.device.company.modules?.includes('health') ? async (a) => {
              try {
                const r = await terminalApi<{ health: HealthData | null }>(p.device, `/terminal/customers/${a.customerId}/health?operatorId=${p.operator.id}`);
                setHealth({ name: a.customerName, h: r.health });
              } catch (e) { setError((e as Error).message); }
            } : undefined}
            pickCustomer={() => new Promise((done) => setPicking(() => done))} />
        </div>
      </div>
      {approving && <ApproveDialog store={p.store} operator={p.operator} why={approving.why}
        onDone={(id) => { approver.current = id; approving.done(!!id); setApproving(null); }} />}
      {picking && <CustomerDialog store={p.store} who={identity(p.device)} onClose={() => { picking(null); setPicking(null); }}
        onPick={(c: Customer) => { picking({ ...(c.id ? { id: c.id } : {}), name: c.name, phone: null }); setPicking(null); }} />}
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
      {health && <HealthDialog name={health.name} h={health.h} onClose={() => setHealth(null)} />}
    </div>
  );
}

interface HealthData { allergies: string | null; conditions: string | null; medications: string | null; pregnant: boolean; notes: string | null }

/** Ficha de saúde (só leitura na caixa; edita-se na gestão). */
function HealthDialog({ name, h, onClose }: { name: string; h: HealthData | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  const row = (l: string, v: string | null) => v && <div><dt className="text-xs font-semibold uppercase tracking-wide text-muted">{l}</dt><dd className="mt-0.5">{v}</dd></div>;
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); onClose(); }} className="m-auto w-[min(520px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">Saúde — {name}</h2>
        <button aria-label="Fechar" onClick={onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="p-5">
        {!h ? <p className="text-muted">Sem ficha de saúde (ou o cliente não deu consentimento).</p> : (
          <dl className="space-y-3">
            {h.pregnant && <p className="rounded-xl bg-amber-100 p-3 font-semibold text-amber-900">Grávida</p>}
            {row('Alergias', h.allergies)}{row('Contra-indicações', h.conditions)}{row('Medicação', h.medications)}{row('Notas', h.notes)}
          </dl>
        )}
        <p className="mt-4 text-xs text-muted">Esta consulta fica registada.</p>
      </div>
    </dialog>
  );
}

/** "Marcar na mesma?": um gerente autoriza (sobreposição ou fora do horário). */
function ApproveDialog({ store, operator, why, onDone }: { store: IdbTerminalStore; operator: Operator; why: string; onDone: (managerId: string | null) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { ref.current?.showModal(); }, []);
  return (
    <dialog ref={ref} onCancel={(e) => { e.preventDefault(); onDone(null); }} className="m-auto w-[min(520px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">Marcar na mesma?</h2>
        <button aria-label="Fechar" onClick={() => onDone(null)} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="p-5">
        <p className="mb-4 rounded-xl bg-amber-100 p-3 text-sm text-amber-900">{why}. Um gerente pode autorizar.</p>
        <ManagerApproval store={store} operator={operator} why={why} onApproved={(m) => onDone(m.id)} />
      </div>
    </dialog>
  );
}
